import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { usePhotostudioGeneration } from '@/hooks/usePhotostudioGeneration';
import { usePhotostudioElements } from '@/hooks/usePhotostudioElements';
import { usePhotostudioProjects } from '@/hooks/usePhotostudioProjects';
import { useDefaultCharacter } from '@/hooks/useDefaultCharacter';

import type { Id } from '../../convex/_generated/dataModel';
import type {
  IPhotostudioSettings,
  IPhotostudioReferences,
  IElementSlotState,
  ElementType,
  IPhotostudioOption,
  VisualPickerDialog,
  IVisualPickerConfig,
} from '@/@types/photostudio';
import {
  getEmptySlotState,
  SHOT_TYPES,
  LIGHTING_TYPES,
  PHOTOGRAPHY_GENRES,
  CAMERA_BODIES,
  FOCAL_LENGTHS,
  LENS_TYPES,
  FILM_STOCKS,
} from '@/@types/photostudio';

function toOptions(values: readonly string[]): IPhotostudioOption[] {
  return values.map(value => ({ value, label: value }));
}

const SHOT_TYPE_OPTIONS = toOptions(SHOT_TYPES);
const LIGHTING_OPTIONS = toOptions(LIGHTING_TYPES);
const CAMERA_BODY_OPTIONS = toOptions(CAMERA_BODIES);
const LENS_TYPE_OPTIONS = toOptions(LENS_TYPES);
const FILM_STOCK_OPTIONS = toOptions(FILM_STOCKS);
const FOCAL_LENGTH_OPTIONS = toOptions(FOCAL_LENGTHS);
const PHOTOGRAPHY_GENRE_OPTIONS = toOptions(PHOTOGRAPHY_GENRES);

export function usePhotostudioForm() {
  const [subjectAction, setSubjectAction] = useState('');
  const [environment, setEnvironment] = useState('');
  const [shotType, setShotType] = useState('');
  const [lightingSource, setLightingSource] = useState('');
  const [photographyGenre, setPhotographyGenre] = useState('');
  const [atmosphere, setAtmosphere] = useState('');
  const [cameraBody, setCameraBody] = useState('');
  const [focalLength, setFocalLength] = useState('');
  const [lensType, setLensType] = useState('');
  const [filmStock, setFilmStock] = useState('');
  const [resolution, setResolution] = useState('1K');
  const [aspectRatio, setAspectRatio] = useState('1:1');

  const [characterSlot, setCharacterSlot] = useState<IElementSlotState>(() => getEmptySlotState());
  const [outfitSlot, setOutfitSlot] = useState<IElementSlotState>(() => getEmptySlotState());
  const [sceneSlot, setSceneSlot] = useState<IElementSlotState>(() => getEmptySlotState());

  const [personPickerOpen, setPersonPickerOpen] = useState(false);
  const [elementPickerOpen, setElementPickerOpen] = useState(false);
  const [elementPickerType, setElementPickerType] = useState<ElementType>('outfit');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [visualPickerOpen, setVisualPickerOpen] = useState<VisualPickerDialog>(null);

  const hasInitializedDefaultRef = useRef(false);

  const generation = usePhotostudioGeneration();
  const { elements } = usePhotostudioElements();
  const { createProject, saveImagesToProject } = usePhotostudioProjects();
  const defaultCharacter = useDefaultCharacter();

  useEffect(() => {
    if (characterSlot.type === 'empty' && defaultCharacter && !hasInitializedDefaultRef.current) {
      setCharacterSlot({
        type: 'reference-person',
        referencePersonId: defaultCharacter.referencePersonId,
      });
      hasInitializedDefaultRef.current = true;
    }
  }, [defaultCharacter, characterSlot.type]);

  const buildReferences = useCallback((): IPhotostudioReferences => {
    const refs: IPhotostudioReferences = {};
    if (characterSlot.type === 'reference-person' && characterSlot.referencePersonId) {
      refs.characterPersonId = characterSlot.referencePersonId;
    }
    if (outfitSlot.type === 'library' && outfitSlot.elementId) {
      refs.outfitId = outfitSlot.elementId;
    }
    if (sceneSlot.type === 'library' && sceneSlot.elementId) {
      refs.sceneId = sceneSlot.elementId;
    }
    return refs;
  }, [characterSlot, outfitSlot, sceneSlot]);

  const buildSettings = useCallback((): IPhotostudioSettings => {
    const settings: IPhotostudioSettings = {
      aspectRatio: aspectRatio as IPhotostudioSettings['aspectRatio'],
    };
    if (shotType) settings.shotType = shotType as IPhotostudioSettings['shotType'];
    if (lightingSource) settings.lighting = lightingSource as IPhotostudioSettings['lighting'];
    if (lensType) settings.lens = lensType as IPhotostudioSettings['lens'];
    if (cameraBody) settings.cameraBody = cameraBody as IPhotostudioSettings['cameraBody'];
    if (filmStock) settings.filmStock = filmStock as IPhotostudioSettings['filmStock'];
    return settings;
  }, [aspectRatio, shotType, lightingSource, lensType, cameraBody, filmStock]);

  const personPreviewUrl = useMemo(() => {
    if (characterSlot.type === 'reference-person' && characterSlot.referencePersonId) {
      return defaultCharacter?.referencePersonId === characterSlot.referencePersonId
        ? defaultCharacter.imageUrl
        : undefined;
    }
    return undefined;
  }, [characterSlot, defaultCharacter]);

  const outfitPreviewUrl = useMemo(() => {
    if (outfitSlot.type === 'library' && outfitSlot.elementId) {
      const element = elements.find(e => e._id === outfitSlot.elementId);
      return element?.imageUrl;
    }
    return undefined;
  }, [outfitSlot, elements]);

  const scenePreviewUrl = useMemo(() => {
    if (sceneSlot.type === 'library' && sceneSlot.elementId) {
      const element = elements.find(e => e._id === sceneSlot.elementId);
      return element?.imageUrl;
    }
    return undefined;
  }, [sceneSlot, elements]);

  const constructedPrompt = useMemo(() => {
    return [
      subjectAction,
      environment,
      shotType && `Shot type: ${shotType}`,
      lightingSource && `Lighting: ${lightingSource}`,
      photographyGenre && `Genre: ${photographyGenre}`,
      atmosphere,
      cameraBody && `Camera: ${cameraBody}`,
      focalLength && `Focal length: ${focalLength}`,
      lensType && `Lens: ${lensType}`,
      filmStock && filmStock !== 'None' && `Film stock: ${filmStock}`,
    ]
      .filter(Boolean)
      .join('. ');
  }, [
    subjectAction,
    environment,
    shotType,
    lightingSource,
    photographyGenre,
    atmosphere,
    cameraBody,
    focalLength,
    lensType,
    filmStock,
  ]);

  const generatedImageUrl = useMemo(() => {
    if (generation.images.length === 0) return null;
    return generation.images[generation.images.length - 1].url;
  }, [generation.images]);

  const handleOpenPersonPicker = useCallback(() => setPersonPickerOpen(true), []);

  const handleOpenElementPicker = useCallback((type: ElementType) => {
    setElementPickerType(type);
    setElementPickerOpen(true);
  }, []);

  const handlePersonSelect = useCallback((personId: Id<'referencePersons'>) => {
    setCharacterSlot({ type: 'reference-person', referencePersonId: personId });
    setPersonPickerOpen(false);
  }, []);

  const handleElementSelect = useCallback(
    (elementId: Id<'photostudioElements'>) => {
      if (elementPickerType === 'outfit') {
        setOutfitSlot({ type: 'library', elementId });
      } else if (elementPickerType === 'scene') {
        setSceneSlot({ type: 'library', elementId });
      }
      setElementPickerOpen(false);
    },
    [elementPickerType]
  );

  const handleClearPerson = useCallback(() => setCharacterSlot(getEmptySlotState()), []);
  const handleClearOutfit = useCallback(() => setOutfitSlot(getEmptySlotState()), []);
  const handleClearScene = useCallback(() => setSceneSlot(getEmptySlotState()), []);

  const handleGenerate = useCallback(async () => {
    const references = buildReferences();
    const settings = buildSettings();
    await generation.generate({
      subject: subjectAction,
      environment,
      settings,
      references,
      count: 1,
      mode: 'variations',
      customPrompt: constructedPrompt,
    });
    if (generation.error) {
      toast.error(generation.error);
    } else {
      toast.success('Image generated successfully');
    }
  }, [buildReferences, buildSettings, subjectAction, environment, constructedPrompt, generation]);

  const handleDownload = useCallback(async () => {
    if (!generatedImageUrl) return;
    try {
      const response = await fetch(generatedImageUrl);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const url = window.URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `photostudio-${Date.now()}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Image downloaded');
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download image');
    }
  }, [generatedImageUrl]);

  const handleSaveToProject = useCallback(
    async (projectIdOrNew: Id<'photostudioProjects'> | 'new', newProjectName?: string) => {
      let imagesToSave = generation.getSelectedImages();
      if (imagesToSave.length === 0) {
        const latestImage = generation.images[generation.images.length - 1];
        if (latestImage) {
          imagesToSave = [{ storageId: latestImage.storageId, url: latestImage.url }];
        } else {
          toast.error('No images to save');
          return;
        }
      }

      setIsSaving(true);
      let projectId: Id<'photostudioProjects'>;

      if (projectIdOrNew === 'new' && newProjectName) {
        const createResult = await createProject(newProjectName);
        if (!createResult.success || !createResult.projectId) {
          toast.error(createResult.error || 'Failed to create project');
          setIsSaving(false);
          return;
        }
        projectId = createResult.projectId;
      } else {
        projectId = projectIdOrNew as Id<'photostudioProjects'>;
      }

      const imageData = imagesToSave.map(img => ({
        storageId: img.storageId,
        prompt: constructedPrompt,
        settings: buildSettings(),
        references: buildReferences(),
      }));

      const saveResult = await saveImagesToProject(projectId, imageData);
      setIsSaving(false);

      if (saveResult.success) {
        toast.success(
          `Saved ${imageData.length} image${imageData.length > 1 ? 's' : ''} to project`
        );
        setSaveDialogOpen(false);
        generation.deselectAll();
      } else {
        toast.error(saveResult.error || 'Failed to save images');
      }
    },
    [
      generation,
      createProject,
      saveImagesToProject,
      constructedPrompt,
      buildSettings,
      buildReferences,
    ]
  );

  const isReadyToGenerate = subjectAction.trim().length > 0 || environment.trim().length > 0;

  const visualPickerConfig = useMemo(() => {
    const configs: Record<Exclude<VisualPickerDialog, null>, IVisualPickerConfig> = {
      shotType: {
        title: 'Shot Type',
        options: SHOT_TYPE_OPTIONS,
        selectedValue: shotType,
        onSelect: setShotType,
        category: 'shot-types',
      },
      lighting: {
        title: 'Lighting',
        options: LIGHTING_OPTIONS,
        selectedValue: lightingSource,
        onSelect: setLightingSource,
        category: 'lighting',
      },
      cameraBody: {
        title: 'Camera Body',
        options: CAMERA_BODY_OPTIONS,
        selectedValue: cameraBody,
        onSelect: setCameraBody,
        category: 'camera-body',
      },
      lensType: {
        title: 'Lens Type',
        options: LENS_TYPE_OPTIONS,
        selectedValue: lensType,
        onSelect: setLensType,
        category: 'lens',
      },
      filmStock: {
        title: 'Film Stock',
        options: FILM_STOCK_OPTIONS,
        selectedValue: filmStock,
        onSelect: setFilmStock,
        category: 'film-stock',
      },
      focalLength: {
        title: 'Focal Length',
        options: FOCAL_LENGTH_OPTIONS,
        selectedValue: focalLength,
        onSelect: setFocalLength,
        category: 'focal-length',
      },
      photographyGenre: {
        title: 'Photography Genre',
        options: PHOTOGRAPHY_GENRE_OPTIONS,
        selectedValue: photographyGenre,
        onSelect: setPhotographyGenre,
        category: 'photography-genre',
      },
    };
    return configs;
  }, [shotType, lightingSource, cameraBody, lensType, filmStock, focalLength, photographyGenre]);

  return {
    subjectAction,
    setSubjectAction,
    environment,
    setEnvironment,
    shotType,
    lightingSource,
    photographyGenre,
    atmosphere,
    setAtmosphere,
    cameraBody,
    focalLength,
    lensType,
    filmStock,
    resolution,
    setResolution,
    aspectRatio,
    setAspectRatio,
    characterSlot,
    outfitSlot,
    sceneSlot,
    personPickerOpen,
    setPersonPickerOpen,
    elementPickerOpen,
    setElementPickerOpen,
    elementPickerType,
    saveDialogOpen,
    setSaveDialogOpen,
    isSaving,
    visualPickerOpen,
    setVisualPickerOpen,
    generation,
    personPreviewUrl,
    outfitPreviewUrl,
    scenePreviewUrl,
    constructedPrompt,
    generatedImageUrl,
    isReadyToGenerate,
    visualPickerConfig,
    handleOpenPersonPicker,
    handleOpenElementPicker,
    handlePersonSelect,
    handleElementSelect,
    handleClearPerson,
    handleClearOutfit,
    handleClearScene,
    handleGenerate,
    handleDownload,
    handleSaveToProject,
  };
}
